// Shared formatting for the Attendance & Treatment Record (ATR), used by the
// public patient form (PublicDirectBillingAttendanceForm) to render the header
// and session rows.
import moment from 'moment';
import DateUtil from '../../../../../utils/DateUtil';
import AppointmentUtil from '../../../../../utils/AppointmentUtil';
import TimezoneUtil from '../../../../../utils/TimezoneUtil';

export const emptySession = () => ({
    appointmentId: null,
    date: '',
    startTime: '',
    endTime: '',
    totalDuration: '',
    cptCodes: '',
    patientSign: '',
    providerSign: '',
});

// Header fields derived from a direct-billing record (same shape on staff + public).
export const buildAttendanceHeader = (directBilling) => {
    const header = {};
    if (!directBilling) return header;

    const p = directBilling.patient;
    if (p) {
        header.patientName = ((p.firstName || '')
            + (p.middleName ? ' ' + p.middleName + ' ' : ' ')
            + (p.lastName || '')).trim();
        header.mrn = p.fullMrn || '';
        if (p.birthday) {
            header.dobAge = DateUtil.formatDate(p.birthday, 'Do MMM YYYY');
        }
        const ins = p.insurance;
        if (ins) {
            header.memberId = ins.memberId || '';
            header.insuranceProvider = [ins.tpa?.name, ins.company?.name]
                .filter(Boolean)
                .join(' / ');
        }
    }

    const diagnosisItems = directBilling.diagnosisGroup?.diagnosisItems || [];
    header.diagnosis = diagnosisItems
        .map(item => item.description ? `${item.code} - ${item.description}` : item.code)
        .filter(Boolean)
        .join(', ');

    header.authorizationNo = directBilling.referenceNo || '';

    const items = directBilling.items || [];
    const approved = items.reduce((max, it) => Math.max(max, it.noOfApprovedSessions || 0), 0);
    header.approvedSessions = approved ? String(approved) : '';

    if (directBilling.creationDate) {
        header.approvalDate = DateUtil.formatDate(directBilling.creationDate, 'Do MMM YYYY');
    }
    if (directBilling.validUntil) {
        header.endDate = DateUtil.formatDate(directBilling.validUntil, 'Do MMM YYYY');
    }

    return header;
};

// Unique "code - description" per CPT item, one per line.
export const buildAttendanceCptCodes = (items) => [...new Set(
    (items || [])
        .map(it => it.cpt)
        .filter(Boolean)
        .map(cpt => cpt.cptCode && cpt.description
            ? `${cpt.cptCode} - ${cpt.description}`
            : (cpt.cptCode || cpt.description))
        .filter(Boolean)
)].join('\n');

// One session row from an appointment. Signatures are passed in (provider from the
// staff profile / DTO; patient from the collected signature) and left blank if absent.
export const appointmentToSession = (appt, cptCodes, providerSign = '', patientSign = '') => {
    const clinicName = appt.room?.clinicName;
    const date = clinicName
        ? TimezoneUtil.atClinicZone(appt.start, clinicName).format('DD-MMM-YYYY')
        : moment(appt.start).format('DD-MMM-YYYY');
    const startTime = clinicName
        ? AppointmentUtil.appointmentStartTime(appt)
        : moment(appt.start).format('hh:mm A');
    const endTime = clinicName
        ? AppointmentUtil.appointmentEndTime(appt)
        : moment(appt.end).format('hh:mm A');
    return {
        ...emptySession(),
        appointmentId: appt.id,
        date,
        startTime,
        endTime,
        totalDuration: String(AppointmentUtil.appointmentDuration(appt)),
        cptCodes,
        patientSign: patientSign || '',
        providerSign: providerSign || '',
    };
};
